﻿// © Microsoft Corporation. All rights reserved.

// Provides Tracking helper methods unique to the NTP and DHP pages.
//
// Need to add OCID to all links that go to the MSN page.
// Also need to add the OCID, CCID and TDUID values to all links that launch the store.
//
// Provide method to modify trackInfo data prior to TrackPage call usage.

import { getLocationHost } from "@msnews/isomorphic-util";
import pageInstance from "./pageInstance";
import { trackInfo } from "./trackInfo";

var displayOptionsHelper = {};
var browserAccess = {};

const locationHost = getLocationHost();
var domainParts = locationHost ? locationHost.split(".").reverse() : [];
var hostDomain = domainParts.length > 1 ? domainParts[1] + "." + domainParts[0] : locationHost; // handle an internal address
var windowsStoreHost = "ms-windows-store";
var domainMatchPattern = /[a-z][a-z0-9+\-.]*:\/\/([a-z0-9\-._~%!$&'()*+,;=]+@)?([a-z0-9\-._~%]+|\[[a-z0-9\-._~%!$&'()*+,;=:]+\])/i;
var ocidMatchPattern = /[&?]ocid=([^&]+)/i;
var ccidMatchPattern = /[&?]ccid=([^&]+)/i;
var classIdentifierForHlTrackingInApps = ".hltxt";

// The following values will not change during the life of the page.
// @ts-ignore
var msnOcid = trackInfo.sitePage.msnocid || "";
// @ts-ignore
var storeOcid = trackInfo.sitePage.storeocid || "";

function addOcidToHref(tHref, target, event) {
    /// <summary>Adds OCID value to MSN URLs and to Store URLs</summary>
    /// <param name="tHref">The href found in track.js</param>
    /// <param name="target">The DOM element being tracked.</param>
    /// <param name="event">The event.</param>

    // MSN OCID is only applied on NTP and DHP pages.
    if (!tHref || !target) {
        return;
    }

    // in case sitePage was not filled when this resolved, get and save the msnocid and storeocid values.
    if (!msnOcid) {
        // @ts-ignore
        msnOcid = trackInfo.sitePage.msnocid;
    }

    if (!storeOcid) {
        // @ts-ignore
        storeOcid = trackInfo.sitePage.storeocid;
    }

    // For the store (Prime and ServiceUI), we need to test the scheme - URL portion before "://".
    var endOfScheme = tHref.indexOf("://");
    var locationOfStore = tHref.indexOf(windowsStoreHost);
    var isStoreUrl = endOfScheme > 0 && locationOfStore >= 0 && locationOfStore < endOfScheme;

    // Need to account for ocid and ccid being of mixed case. e.g. parameters.ocid returns null if the
    // param is "Ocid".
    var ocidMatch = tHref.match(ocidMatchPattern);
    var ccidMatch = tHref.match(ccidMatchPattern);

    // Fix 1764836. Ad links (e.g. EMS Ads) already contain their own ocid (not ours) on the URL. We need to make sure to keep their ocid value
    // and add the ccid value.  Some Ads use uppercase OCID parameter.
    // NOTE: JS doesn't have negative lookback so selecting values directly doesn't work. Using [1]
    // to read the second match which will be the value.
    var ocidOnUrl = ocidMatch && ocidMatch[1];
    var ccidOnUrl = ccidMatch && ccidMatch[1];
    var emsOnUrl;

    if (ocidOnUrl && ccidOnUrl) {
        // If this is an EMS Ad it will already have the ocid and ccid values. We need to add our other values.
        var initStoreUrl = tHref;
        var emsMatch = tHref.match(/[&?]ocid=(ems\.)/i);
        emsOnUrl = emsMatch && emsMatch[1];

        // See if we are an EMS link and do not have emsadid on the URL.  If already have one do not add another.
        if (emsOnUrl && !initStoreUrl.match(/[=_]emsadid\./i)) {
            initStoreUrl = addCcidToStoreUrl(initStoreUrl.replace(ccidMatchPattern, ''), ccidMatch[1]);
        }

        // This has already been modified.
        return;
    }

    var hashIndex = tHref.indexOf("#");
    var hashValue = "";

    // Do not modify Prime page URLs - maintain this as only DHP/NTP code.
    // If we do not have an ocid on the url.
    if (!ocidOnUrl) {
        // TopSites uses an href of "#" to indicate it should not be a navigation, but it gets changed to the NTP Url with # at the end.
        // Some Prime websites also use the # character and we should not affect them, so make this check within the ServiceUI IFDEF.
        if (hashIndex === tHref.length - 1) {
            return;
        }

        // We need the msnocid value if this is the NTP/DHP page.
        if (msnOcid) {
            var domainMatch = domainMatchPattern.exec(tHref);
            var hrefHostName = domainMatch && domainMatch[2];
            var linkUrl = tHref;
            if (hrefHostName) {
                var isMsnDomain = hrefHostName.indexOf(hostDomain) >= 0;
                if (isMsnDomain) {
                    if (hashIndex >= 0) {
                        hashValue = tHref.substring(hashIndex, tHref.length);
                        linkUrl = tHref.substring(0, hashIndex);
                    }

                    linkUrl += linkUrl.indexOf('?') > 0 ? "&" : "?";
                    linkUrl = linkUrl + "ocid=" + msnOcid;
                    target.href = linkUrl + (hashValue || "");
                    return;
                }
            }
        }
    }

    // If we don't have the store OCID value, just return. We can't add the ocid if we don't have the correct value.
    if (!ocidOnUrl && !storeOcid) {
        return;
    }

    if (isStoreUrl) {
        // Bug 1763626 - clicking Top Sites Open App link multiple times added OCID/CCID multiple times.
        // If there was a hash at the end of the URL, we used to add the parameters after the hash, and it was
        // not recognized as parameters by navigation.getParamsFromUrl(tHref).
        // If there is a hash value, save it, and re-add it at the end.
        if (hashIndex >= 0) {
            hashValue = tHref.substring(hashIndex, tHref.length);
            tHref = tHref.substring(0, hashIndex);
        }

        // We know that the store URL will have a query string parameter (the ID or PFN).
        // If the url already has an ocid on it, don't add another.
        var storeUrl = tHref;
        if (!ocidOnUrl) {
            storeUrl += "&ocid=" + storeOcid;
        }

        storeUrl = addCcidToStoreUrl(storeUrl, emsOnUrl);
        target.href = storeUrl + hashValue;
    }
}

function addCcidToStoreUrl(tHref, emsAdId) {
    ///<summary>Adds Ccid to the Store url.</summary>
    /// <param name="tHref">The href found in track.js</param>
    /// <param name="tHref">The optional value for the emsid ccid value</param>
    /// <return>Returns Store Url with ccid.</return>

    var ccidMatch = tHref.match(ccidMatchPattern);
    var ccidOnUrl = ccidMatch && ccidMatch[1];
    var hashIndex = tHref.indexOf("#");
    var hashValue = "";

    // If the url already has an ccid on it, don't add another, but make sure it has MUID and RID. EMS Ads already have a ocid and ccid values.
    if (!ccidOnUrl) {
        // TODO: refactor this code , so we don't have to calculate index at different places
        if (hashIndex >= 0) {
            hashValue = tHref.substring(hashIndex, tHref.length);
            tHref = tHref.substring(0, hashIndex);
        }

        // We know that the store URL will have a query string parameter (the ID or PFN).
        // If the url already has an ocid on it, don't add another.
        var storeUrl = tHref;
        storeUrl += "&ccid=";

        var ccidData = [];

        // If sent an emsAdId value add it first.
        if (emsAdId) {
            ccidData.push("emsadid." + emsAdId);
        }

        // @ts-ignore
        var webig = pageInstance.getRequestId() || trackInfo.sitePage.requestId;
        if (webig) {
            ccidData.push("webig." + webig);
        }

        var muid = trackInfo.client.clientId();
        if (muid) {
            ccidData.push("muid." + muid);
        }

        return storeUrl + ccidData.join("_") + hashValue;
    }

    return tHref;
}

function modifyValuesForTrackPage() {
    ///<summary>Modifies specific trackInfo values for TrackPage, to identify user settings.</summary>
    /// <return>Returns whether we need to prevent the ComScore beacon from firing.</return>
    var noContent = "_NoContent";
    var topSites = "-topsites";
    var oneBox = "-onebox";

    // If we need to prevent sending a beacon, return true.
    var preventComscore = false;

    if (trackInfo.sitePage) {
        var hasContent = true;
        // when there is rive content

        if (!hasContent) {
            // We cannot send a ComScore beacon if we have no content.
            preventComscore = true;
        }
    }

    // add personalization specific properties if they haven't been created before.
    if (trackInfo.client
        // @ts-ignore
        && !trackInfo.client.personalization) {
        // add any Spartan DHP/NTP specific personalization related properties to this object
        var props;

        // write out the value only if we have any properties set in the property bag
        if (props) {
            // @ts-ignore
            trackInfo.client.personalization = JSON.stringify(props);
        }
    }

    return { preventComscore: preventComscore };
}

function attachTaglineClickHandler(element) {
    /// <summary>Modify "hl" before the trackEvent.</summary>
    /// <param name="element">The element (or jquery collection) to process.</param>
    /// <remark>We do not want to require jquery.</remark>

    // If this code becomes needed for Prime, change the IFDEF. Also using a switch if this is a perf issue.
}

function getActionResult(action, isSuccessful) {
    /// <summary>
    ///     Gets the action result name based on the action and the fact whether it was successfully executed or not
    /// </summary>
    /// <param name="action" type="String" optional="false">
    ///     The name of the action (e.g. sethome, addfavorite,...)
    /// </param>
    /// <param name="isSuccessful" type="Boolean" optional="true">
    ///     Flag to indicate whether the action was successfully executed or not
    /// </param>
    return isSuccessful ? action + "_succeeded" : action + "_failed";
}

export default {
    addOcidToHref: addOcidToHref,
    modifyValuesForTrackPage: modifyValuesForTrackPage,
    attachTaglineClickHandler: attachTaglineClickHandler,
    addCcidToStoreUrl: addCcidToStoreUrl,
    getActionResult: getActionResult
};
